import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material'
import {
  Build,
  CarRepair,
  LocationOn,
} from '@mui/icons-material'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { PickupLocation, ShopType } from '@otw/models'
import type { RepairShopTruckCounts } from '@/hooks/logistics/useLogistics'

import {
  GOOGLE_MAPS_LOADER_ID,
  GOOGLE_MAPS_LIBRARIES,
  getGoogleMapsApiKey,
} from '@/constants/googleMaps'

// Extend PickupLocation to include shop type for repair shops
interface RepairShopLocation extends PickupLocation {
  shop_type?: ShopType[]
  name?: string
  is_active?: boolean
  inactive_at?: Date | null
  inactive_note?: string | null
}

/** Mileage options for map search radius (miles) */
export const SEARCH_RADIUS_MILE_OPTIONS = [25, 50, 75, 100, 150, 200, 300] as const

interface LocationMapProps {
  locations: (PickupLocation | RepairShopLocation)[]
  locationType: 'pickup' | 'repair_shop'
  onLocationClick?: (location: PickupLocation | RepairShopLocation) => void
  /** Optional: locationId -> count of active trucks (used for repair shop marker badges). */
  activeTruckCountsByLocationId?: Record<string, number>
  /** Optional: at-shop and en-route truck counts per repair shop (for marker tooltips). */
  repairShopTruckCountsById?: Record<string, RepairShopTruckCounts>
  /** When set, map centers on this point and shows suggested shops by distance (repair shop map only) */
  searchCenter?: { lat: number; lng: number } | null
  searchAddressDisplay?: string
  /** Max distance (miles) for suggested shops when search is active. Default 100. */
  searchRadiusMiles?: number
  /** Called when suggested shops (within searchRadiusMiles) are computed; pass [] when no search */
  onSuggestedShops?: (shops: SuggestedShop[]) => void
  /**
   * Called once per location that had to be geocoded, so the caller can persist
   * the result and skip the Geocoding API on subsequent loads.
   */
  onGeocoded?: (location: PickupLocation | RepairShopLocation, coords: GeocodedCoords) => void
}

export interface GeocodedCoords {
  lat: number
  lng: number
  /** Address the coordinates were resolved from. */
  address: string
}

/** Full address string used both for geocoding and for cache invalidation. */
const buildFullAddress = (location: PickupLocation | RepairShopLocation): string =>
  `${location.address}, ${location.city}, ${location.state} ${location.zip}`

/** Cached coordinates, or null when absent or stale relative to the current address. */
const getCachedCoords = (
  location: PickupLocation | RepairShopLocation,
): { lat: number; lng: number } | null => {
  const { lat, lng, geocoded_address: geocodedAddress } = location
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  if (geocodedAddress && geocodedAddress !== buildFullAddress(location)) return null
  return { lat, lng }
}

// Static libraries array to prevent unnecessary reloads
const US_MAP_CENTER = { lat: 39.8283, lng: -98.5795 }
const GEOLOCATION_TIMEOUT_MS = 5000
const GEOCODE_TIMEOUT_MS = 8000

/** Max distance for suggested shops (100 miles) in meters */
export const SUGGESTED_SHOPS_MAX_MILES = 100
export const SUGGESTED_SHOPS_MAX_METERS = SUGGESTED_SHOPS_MAX_MILES * 1609.344

/** Format distance in meters to human-readable string */
export const formatDistance = (meters: number): string => {
  const miles = meters / 1609.344
  if (miles < 0.1) return `${Math.round(meters)} m`
  if (miles < 10) return `${miles.toFixed(1)} mi`
  return `${Math.round(miles)} mi`
}

export interface SuggestedShop {
  location: PickupLocation | RepairShopLocation
  distanceMeters: number
}

const LocationMap: React.FC<LocationMapProps> = ({
  locations,
  locationType,
  onLocationClick,
  activeTruckCountsByLocationId = {},
  repairShopTruckCountsById = {},
  searchCenter = null,
  searchAddressDisplay = '',
  searchRadiusMiles = SUGGESTED_SHOPS_MAX_MILES,
  onSuggestedShops,
  onGeocoded,
}) => {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number }>(US_MAP_CENTER)
  const [mapMarkers, setMapMarkers] = useState<Array<{
    id: string
    position: { lat: number; lng: number }
    location: PickupLocation | RepairShopLocation
  }>>([])
  const [isGeocoding, setIsGeocoding] = useState(false)

  // Google Maps setup - use same ID as other maps to share the loader instance
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const [map, setMap] = useState<google.maps.Map | null>(null)

  const theme = useTheme()

  const getShopTypeColor = (shopTypes?: ShopType[]) => {
    if (!shopTypes || shopTypes.length === 0) return theme.palette.text.secondary
    const hasMechanic = shopTypes.includes('mechanic_shop')
    const hasBody = shopTypes.includes('body_shop')
    if (hasMechanic && hasBody) return theme.palette.info.main
    const firstType = shopTypes[0]
    switch (firstType) {
      case 'mechanic_shop':
        return theme.palette.primary.main
      case 'body_shop':
        return theme.palette.secondary.main
      case 'parts':
        return theme.palette.info.main
      case 'other':
        return theme.palette.text.secondary
      case 'mobile':
        return theme.palette.success.main
      default:
        return theme.palette.text.secondary
    }
  }

  const getShopTypeLabel = (shopTypes?: ShopType[]) => {
    if (!shopTypes || shopTypes.length === 0) return 'Unknown'
    const labels: Record<ShopType, string> = {
      mechanic_shop: 'Mechanic',
      body_shop: 'Body Shop',
      parts: 'Parts',
      other: 'Other',
      mobile: 'Mobile',
    }
    return shopTypes.map(type => labels[type]).join(', ')
  }

  const geocodeAddress = useCallback(async (address: string): Promise<{ lat: number; lng: number } | null> => {
    const apiKey = getGoogleMapsApiKey()
    if (!apiKey) return null

    const fetchCoords = async (): Promise<{ lat: number; lng: number } | null> => {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`,
        )
        const data = await response.json()
        if (data.results && data.results.length > 0) {
          const { lat, lng } = data.results[0].geometry.location
          return { lat, lng }
        }
      } catch (error) {
        console.error('Error geocoding address:', error)
      }
      return null
    }

    return Promise.race([
      fetchCoords(),
      new Promise<null>(resolve => setTimeout(() => resolve(null), GEOCODE_TIMEOUT_MS)),
    ])
  }, [])

  // Shops within searchRadiusMiles, sorted by distance (when search is active)
  const searchRadiusMeters = searchRadiusMiles * 1609.344
  const shopsWithDistance = useMemo(() => {
    if (!searchCenter || !window.google?.maps?.geometry || mapMarkers.length === 0) return []
    const from = new google.maps.LatLng(searchCenter.lat, searchCenter.lng)
    const withDist = mapMarkers.map((m) => {
      const to = new google.maps.LatLng(m.position.lat, m.position.lng)
      const meters = google.maps.geometry.spherical.computeDistanceBetween(from, to)
      return { location: m.location, distanceMeters: meters }
    })
    const withinRange = withDist
      .filter((s) => s.distanceMeters <= searchRadiusMeters)
      .filter((s) => (s.location as RepairShopLocation).is_active !== false)
    withinRange.sort((a, b) => a.distanceMeters - b.distanceMeters)
    return withinRange
  }, [searchCenter, mapMarkers, searchRadiusMeters])

  // Notify parent when suggested shops list changes
  useEffect(() => {
    if (!searchCenter) {
      onSuggestedShops?.([])
      return
    }
    onSuggestedShops?.(shopsWithDistance)
  }, [searchCenter, shopsWithDistance, onSuggestedShops])

  // Prefer user location when available; fall back to US center.
  useEffect(() => {
    if (!navigator.geolocation) return

    let settled = false
    const finish = (coords: { lat: number; lng: number }) => {
      if (settled) return
      settled = true
      setUserLocation(coords)
    }

    const timeoutId = window.setTimeout(() => finish(US_MAP_CENTER), GEOLOCATION_TIMEOUT_MS)

    navigator.geolocation.getCurrentPosition(
      position => {
        window.clearTimeout(timeoutId)
        finish({ lat: position.coords.latitude, lng: position.coords.longitude })
      },
      error => {
        console.error('Error getting user location:', error)
        window.clearTimeout(timeoutId)
        finish(US_MAP_CENTER)
      },
      { timeout: GEOLOCATION_TIMEOUT_MS - 500, maximumAge: 60000 },
    )

    return () => window.clearTimeout(timeoutId)
  }, [])

  // Process locations for map markers - similar pattern to PaidVehiclesGrid
  const processedDataRef = useRef<string>('')

  // Extract and geocode locations when they change
  useEffect(() => {
    if (!isLoaded || !locations || locations.length === 0) {
      if (locations && locations.length === 0) {
        setMapMarkers([])
        processedDataRef.current = ''
      }
      return
    }

    // Create a hash of the current data to avoid reprocessing
    const dataHash = `${locations.length}-${locations.map(loc => loc.id || `${loc.address}-${loc.city}-${loc.state}-${loc.zip}`).join(',')}`
    
    // Skip if we've already processed this data
    if (processedDataRef.current === dataHash) {
      return
    }

    processedDataRef.current = dataHash

    // Locations with a valid cached geocode render immediately; only the rest
    // cost a Geocoding API call, and those results are handed back for caching.
    const markers: Array<{
      id: string
      position: { lat: number; lng: number }
      location: PickupLocation | RepairShopLocation
    }> = []
    const needsGeocoding: (PickupLocation | RepairShopLocation)[] = []

    locations.forEach((location, index) => {
      if (!location.address) {
        console.warn('Location missing address:', location)
        return
      }
      const cached = getCachedCoords(location)
      if (cached) {
        markers.push({
          id: location.id || `location-${index}`,
          position: cached,
          location,
        })
      } else {
        needsGeocoding.push(location)
      }
    })

    setMapMarkers(markers)

    if (needsGeocoding.length === 0) {
      setIsGeocoding(false)
      return
    }

    setIsGeocoding(true)
    let cancelled = false

    const processLocations = async () => {
      for (const location of needsGeocoding) {
        if (cancelled) return
        const fullAddress = buildFullAddress(location)
        const coords = await geocodeAddress(fullAddress)

        if (!coords) {
          console.warn('Failed to geocode address:', fullAddress)
          continue
        }

        onGeocoded?.(location, { ...coords, address: fullAddress })

        if (cancelled) return
        setMapMarkers(prev => [
          ...prev,
          {
            id: location.id || `location-${prev.length}`,
            position: coords,
            location,
          },
        ])
      }
      if (!cancelled) setIsGeocoding(false)
    }

    processLocations()

    return () => {
      cancelled = true
    }
  }, [isLoaded, locations, geocodeAddress, onGeocoded])

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    if (userLocation) {
      map.setCenter(userLocation)
    }
    setMap(map)
    
    // Fit map to show all markers when they're loaded
    if (mapMarkers.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      mapMarkers.forEach(marker => {
        bounds.extend(marker.position)
      })
      map.fitBounds(bounds)
    }
  }, [userLocation, mapMarkers])

  const onUnmount = useCallback(function callback(map: google.maps.Map) {
    setMap(null)
  }, [])

  // Update map bounds when markers change; when search is active, center on search and fit bounds to include search + markers
  useEffect(() => {
    if (!map) return
    if (searchCenter) {
      map.setCenter(searchCenter)
      const bounds = new google.maps.LatLngBounds()
      bounds.extend(searchCenter)
      mapMarkers.forEach(marker => {
        bounds.extend(marker.position)
      })
      map.fitBounds(bounds)
      const listener = google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        if (map.getZoom() && map.getZoom()! > 14) {
          map.setZoom(14)
        }
      })
      return () => {
        google.maps.event.removeListener(listener)
      }
    }
    if (mapMarkers.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      mapMarkers.forEach(marker => {
        bounds.extend(marker.position)
      })
      map.fitBounds(bounds)
      const listener = google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        if (map.getZoom() && map.getZoom()! > 15) {
          map.setZoom(15)
        }
      })
      return () => {
        google.maps.event.removeListener(listener)
      }
    } else if (userLocation) {
      map.setCenter(userLocation)
      map.setZoom(10)
    }
  }, [map, mapMarkers, userLocation, searchCenter])

  // Get marker icon based on location type
  const getMarkerIcon = (location: PickupLocation | RepairShopLocation) => {
    if (locationType === 'repair_shop') {
      const activeCount = location.id ? activeTruckCountsByLocationId[location.id] ?? 0 : 0
      const badgeFill = getShopTypeColor((location as RepairShopLocation).shop_type)
      const badgeText = activeCount > 99 ? '99+' : String(activeCount)
      const fontSize = badgeText.length > 2 ? 8 : 10

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="34" height="34" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
          <circle cx="17" cy="17" r="11.5" fill="${badgeFill}" stroke="white" stroke-width="2"/>
          <text x="17" y="20.5" text-anchor="middle" font-size="${fontSize}" font-family="Arial, sans-serif" font-weight="700" fill="white">
            ${badgeText}
          </text>
        </svg>
      `)}`
    }

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" fill="${theme.palette.primary.main}" stroke="white" stroke-width="1.5"/>
      </svg>
    `)}`
  }

  const getRepairShopTruckCountSuffix = (locationId?: string): string => {
    if (!locationId) return ''
    const counts = repairShopTruckCountsById[locationId]
    const atShop = counts?.atShop ?? 0
    const enRoute = counts?.enRoute ?? 0
    const total = atShop + enRoute
    if (total <= 0) return ''
    if (enRoute > 0) {
      return ` • ${atShop} in repair, ${enRoute} on the way`
    }
    return ` • ${atShop} truck${atShop === 1 ? '' : 's'} in repair`
  }

  // Get marker title
  const getMarkerTitle = (location: PickupLocation | RepairShopLocation) => {
    if (locationType === 'repair_shop') {
      const repairLocation = location as RepairShopLocation
      const shopType = getShopTypeLabel(repairLocation.shop_type)
      const name = repairLocation.name ? ` - ${repairLocation.name}` : ''
      const activeCountSuffix = getRepairShopTruckCountSuffix(location.id)
      return `${shopType}${name}: ${location.address}, ${location.city}, ${location.state}${activeCountSuffix}`
    }
    return `Pickup Location: ${location.address}, ${location.city}, ${location.state}`
  }

  if (!getGoogleMapsApiKey()) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', height: 400 }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Google Maps API key is not configured
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set REACT_APP_GOOGLE_MAPS_API_KEY in the app environment.
        </Typography>
      </Paper>
    )
  }

  if (!isLoaded) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', height: 400 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading map...
        </Typography>
      </Paper>
    )
  }

  if (loadError) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', height: 400 }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Error loading Google Maps
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please check your API key configuration
        </Typography>
      </Paper>
    )
  }

  if (!locations || locations.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', height: 400 }}>
        <LocationOn sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No {locationType === 'pickup' ? 'pickup' : 'repair shop'} locations found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add locations to see them on the map
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {locationType === 'pickup' ? 'Pickup Locations' : 'Repair Shops'} Map
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {locationType === 'repair_shop' && (
            <>
              <Chip
                icon={<Build />}
                label="Mechanic Shops"
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                icon={<CarRepair />}
                label="Body Shops"
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label="Both"
                size="small"
                color="info"
                variant="outlined"
              />
            </>
          )}
          <Chip
            icon={<LocationOn />}
            label={`${locations.length} location${locations.length !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
          {isGeocoding ? (
            <Chip
              label="Geocoding locations..."
              size="small"
              color="info"
              variant="outlined"
            />
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'block',
          width: '100%',
          height: '800px',
        }}
      >
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={userLocation}
          zoom={4}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }]
              }
            ]
          }}
        >
          {/* Search address marker */}
          {searchCenter && (
            <Marker
              position={searchCenter}
              icon={{
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                  <svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path fill="${theme.palette.error.main}" stroke="white" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle cx="12" cy="9" r="2.5" fill="white"/>
                  </svg>
                `)}`,
                scaledSize: new google.maps.Size(28, 28),
                anchor: new google.maps.Point(14, 14),
              }}
              title={`Searched: ${searchAddressDisplay}`}
            />
          )}
          {/* Location Markers */}
          {mapMarkers.map(marker => {
            const activeCount =
              locationType === 'repair_shop' && marker.location.id
                ? activeTruckCountsByLocationId[marker.location.id] ?? 0
                : 0
            const markerSize = locationType === 'repair_shop' ? 34 : 24
            const markerAnchor = locationType === 'repair_shop' ? 17 : 12

            return (
              <Marker
                key={marker.id}
                position={marker.position}
                icon={{
                  url: getMarkerIcon(marker.location),
                  scaledSize: new google.maps.Size(markerSize, markerSize),
                  anchor: new google.maps.Point(markerAnchor, markerAnchor),
                }}
                title={getMarkerTitle(marker.location)}
                onClick={() => {
                  if (onLocationClick) {
                    onLocationClick(marker.location)
                  }
                }}
              />
            )
          })}
        </GoogleMap>
      </Box>
    </Paper>
  )
}

export default LocationMap 